import type { MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useCurrentUser } from '@/shared/api/currentUser'
import { useFavoriteIds, useToggleFavorite } from '@/shared/api/favoritesQueries'
import { cn } from '@/shared/lib/utils'
import { HORIZON_ICON_REGISTRY } from '../../icons'
import type { FavoriteHeartButtonProps } from '@/shared/components/FavoriteHeartButtonView'

export function FavoriteHeartButton({
  propertyId,
  variant = 'chip',
  className,
  isFavorite: isFavoriteProp,
}: FavoriteHeartButtonProps) {
  const { t } = useTranslation()
  const { data: currentUser } = useCurrentUser()
  const { data: favoriteIds } = useFavoriteIds()
  const toggle = useToggleFavorite()
  const HeartIcon = HORIZON_ICON_REGISTRY.heart

  if (!currentUser?.id) {
    return null
  }

  const isFavorite = isFavoriteProp === true || (favoriteIds?.has(propertyId) ?? false)

  const onClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    toggle.mutate({ propertyId, isFavorite })
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={toggle.isPending}
      aria-pressed={isFavorite}
      aria-label={isFavorite ? t('favorites.remove') : t('favorites.add')}
      className={cn(
        'horizon-favorite-heart inline-flex items-center justify-center rounded-pill transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60',
        variant === 'overlay'
          ? 'absolute top-3 right-3 z-[var(--brand-z-sticky)] size-11 bg-card/90 text-primary shadow-[var(--brand-shadow-1)] hover:bg-card'
          : 'min-h-7 gap-1.5 border px-2.5 text-xs font-medium text-primary hover:bg-muted/50',
        className,
      )}
    >
      <HeartIcon
        className="size-4 shrink-0"
        weight={isFavorite ? 'fill' : 'regular'}
        aria-hidden
      />
      {variant === 'chip' ? (
        <span>{isFavorite ? t('favorites.saved_label') : t('favorites.add_label')}</span>
      ) : null}
    </button>
  )
}
